import { useGetOrganizationImpactedPaymentPlanAccountsQuery } from '@/services/organizations';
import { Box, Spinner, Table, FormikRadioGroup } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { ClientPaymentPlan } from 'types';

const UpdateImpactedAccountsInput = ({
  currentPaymentPlanId,
  isSubmitting,
  paymentPlans,
}: {
  currentPaymentPlanId?: string;
  isSubmitting: boolean;
  paymentPlans: ClientPaymentPlan[];
}) => {
  const { setFieldValue, values }: any = useFormikContext();
  useEffect(() => {
    setFieldValue('planToApplyToExistingAccounts', values.planToApplyToExistingAccounts);
  }, [setFieldValue, values.planToApplyToExistingAccounts]);

  const getPaymentPlanName = (paymentPlanId: string | undefined, paymentPlans: ClientPaymentPlan[]) => {
    if (paymentPlanId === undefined || paymentPlanId === '') return '';
    const paymentPlan = paymentPlans.find((plan) => String(plan.id) === paymentPlanId);
    return paymentPlan ? paymentPlan.name : '';
  };
  return (
    <Box childGap="xs">
      <Field
        title={'Proposal for Accounts impacted by this change'}
        name="planToApplyToExistingAccounts"
        id="planToApplyToExistingAccounts"
        options={[
          {
            id: `planToApplyToExistingAccounts-${currentPaymentPlanId}`,
            value: currentPaymentPlanId,
            label: `${getPaymentPlanName(currentPaymentPlanId, paymentPlans)} - assign current plan`,
          },
          {
            id: `planToApplyToExistingAccounts-${values.paymentPlanId}`,
            value: values.paymentPlanId,
            label: `${getPaymentPlanName(values.paymentPlanId, paymentPlans)} - assign new plan selected`,
          },
        ]}
        component={FormikRadioGroup}
        isRequired
        isDisabled={isSubmitting}
      />
    </Box>
  );
};

export const PaymentPlanChangeImpactedAccounts = ({
  formValues,
  currentPaymentPlanId,
  alias,
  paymentPlans,
  isSubmitting,
  setTotalImpactedAccounts,
}: {
  formValues: any;
  currentPaymentPlanId: string;
  alias: string;
  paymentPlans: ClientPaymentPlan[];
  isSubmitting: boolean;
  setTotalImpactedAccounts: (total: number) => void;
}) => {
  const { data, isLoading, isFetching, isSuccess, error } = useGetOrganizationImpactedPaymentPlanAccountsQuery(
    { proposedPaymentPlanId: formValues.paymentPlanId, currentPaymentPlanId: currentPaymentPlanId, alias },
    {
      skip: !formValues.paymentPlanId,
    },
  );
  useEffect(() => {
    if (isSuccess && data) {
      setTotalImpactedAccounts(data.totalAccountsImpacted || 0);
    }
  }, [isSuccess, data, setTotalImpactedAccounts]);
  return (
    <>
      {!isLoading && error && (
        <Box margin={'0 0 lg 0'} color="danger">
          Error loading impacted accounts for payment plan change
        </Box>
      )}
      {!isLoading && !isFetching && !error && (
        <Box childGap="md">
          <Box direction="row" childGap="xs">
            <Box style={{ color: 'var(--color-brand-grey-600)', fontWeight: 575 }}>
              Accounts Impacted by payment plan change:
            </Box>
            <Box>
              {data && data?.accounts.length > 0
                ? `Showing ${data?.accounts.length} out of
            ${data?.totalAccountsImpacted || 0} total accounts`
                : 0}
            </Box>
          </Box>
          {(data?.totalAccountsImpacted || 0) > 0 && (
            <Box childGap="xl">
              <Box style={{ maxHeight: '550px', width: '100%' }}>
                <Table
                  hasStickyHeader
                  rowKey="id"
                  columns={[
                    {
                      heading: 'Account',
                      dataKey: 'id',
                      render: (_cell: any, row: any) => <Link to={`/accounts/${row.id}`}>{row.name}</Link>,
                    },
                    { heading: 'Current Milestone', dataKey: 'currentMilestone' },
                    { heading: 'Milestone Status', dataKey: 'milestoneStatus' },
                  ]}
                  rows={data?.accounts || []}
                />
              </Box>
              <Box>
                <UpdateImpactedAccountsInput
                  paymentPlans={paymentPlans}
                  isSubmitting={isSubmitting}
                  currentPaymentPlanId={currentPaymentPlanId}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
      {isLoading && (
        <Box childGap="md">
          <Box>Loading impacted accounts...</Box>
          <Spinner size="lg" />
        </Box>
      )}
    </>
  );
};
